import dayjs from 'dayjs';
import { useQuery } from 'react-query';

import type { IUser } from 'src/api/users-api';
import type { IGetConversationsResponse } from 'src/services/api/messages/use-conversations';
import { apiClient } from 'src/services/api/client';
import Auth from 'src/services/api/auth/Auth';
import { useUpdateConversations } from 'src/services/push-notifications/use-update-conversations';

export const usePollConversations = (
    loggedInUser: IUser | undefined,
    isLive: boolean,
) => {
    const updateConversations = useUpdateConversations();

    const jwtPayload = Auth.jwtPayload;
    const isBypassPaywall = Boolean(jwtPayload?.ibp);
    const isTeamActive = Boolean(loggedInUser?.team?.is_active);
    const isPaywallEnabled = !isBypassPaywall && !isTeamActive;
    const isOnboardingComplete = Boolean(loggedInUser?.is_onboarding_complete);
    const enabled = isOnboardingComplete && !isPaywallEnabled;

    const query = useQuery(
        [
            'poll-conversations',
            loggedInUser?.uuid,
            isPaywallEnabled ? 'paywall' : 'no-paywall',
        ],
        async () => {
            const res = await apiClient('api/landlord/conversations', {
                searchParams: {
                    is_archived: false,
                    // This sends the correct datetime that is the user's local time converted to UTC
                    // Removing the timezone part, because backend cannot handle dates with timezones
                    since: dayjs().subtract(30, 'seconds').toISOString().split('Z')[0],
                },
            }).json<IGetConversationsResponse>();

            if (!res.conversations.length) {
                return;
            }

            updateConversations(res.conversations);
        },
        {
            enabled,
            refetchInterval: isLive ? false : 15_000,
        },
    );

    return { ...query, enabled };
};
